import { Col, Divider, Row, Select } from "antd";
import React from "react";
import CardContent from "../CardContent";
import CardData from "../CardData";
import CardDescription from "../CardDescription";
import CardGraph from "../CardGraph";
import CardMenu from "../CardMenu";
import CardTitle from "../CardTitle";
import StyledSelect from "../StyledSelect";
import CardMap from "../CardMap";
import DownloadButton from "../DownloadButton";
//import translations from "../../../utils/text/term_translations.json";
import ErrorLoading from "../../404/ErrorLoading";
import { getMonthText } from "../../../utils/functions/getMonthText";
import GlossaryModal from "../../GlossaryModal/GlossaryModal";
import { QuestionCircleOutlined } from "@ant-design/icons";
import MISC_TEXT from "../../../utils/text/misc.json";

const { Option } = Select;

/**
 *
 * @param {Object} countryProps Object with props collected from COUNTRY_OPTIONS for the needed country
 * @param {Object} dateOptions Object or tree with options for available years and months
 * @param {Boolean} isLoading Boolean to know if graph data is finished loading
 * @param {Boolean} isDataAvailable Boolean to know if data was returned from backend
 * @param {Object} options Object with options object for echarts graph
 * @param {String} selectedYear String code for selected year in component
 * @param {any} lastMonthData String or Number for description data
 * @param {string} cardTitle String with title of card used for headging
 * @param {string} methodolgy String or JSX for methodology tooltip
 * @param {string} dataSubtitle String or JSX for subtitle in description data
 * @param {string} descripiton String or JSX for description text
 * @param {function} handleYearChange Function: Setter for year state in parent component
 *
 * @returns {JSX} JSX -- Complete Card component contents
 */
export default function CardOnlyYear({
  countryProps,
  dateOptions,
  isLoading,
  isDataAvailable,
  errorLoading,
  options,
  lastMonthData,
  dataDate,
  cardTitle,
  methodology,
  dataSubtitle,
  description,
  selectArray,
  geoJSONData,
  selectedDataType,
  mapData,
  isMapJsonReady,
  language,
  includesGlossary,
  includesCountrySelect,
  handleCountrySelectChange,
  countrySelectDefaultValue,
  countrySelectOptions,
  downloadableCardID,
  downloadableCardFileName,
  downloadableCardTitle,
  downloadableCardSubtitle,
  hideDownload,
  borderRadius,
}) {
  function translateMonths(options) {
    if (options && options.xAxis) {
      options.xAxis.data = options.xAxis.data.map((d) => {
        return d.replaceAll('T', 'Q');
      });
    }
    return options;
  }

  return (
    <Row style={{ height: "100%" }}>
      <Col xs={24} lg={9}>
        <CardMenu>
          <div>
            <CardTitle
              title={cardTitle}
              methodology={methodology}
              language={language}
            />
            <Divider className="smaller-margins" />
            <CardData
              data={lastMonthData}
              subtitle={dataSubtitle}
              date={
                dataDate ??
                (language === "en" ? "Date" : "Fecha") +
                  ": " +
                  (dateOptions.mostRecentDate.month
                    ? getMonthText(
                        dateOptions.mostRecentDate.month,
                        language,
                        false
                      )
                    : "--") +
                  ", " +
                  (dateOptions.mostRecentDate.year || "--")
              }
              lang={language}
            />

            {selectArray.map((select, key) => {
              return (
                <StyledSelect
                  key={key}
                  selectedValue={select.selectedValue}
                  changeHandler={select.changeHandler}
                  options={select.options}
                  placeHolderText={select.placeHolderText}
                  labelAttributeName={select.labelAttributeName}
                  valueAttributeName={select.valueAttributeName}
                  language={language}
                  ignoreTranslation={select.ignoreTranslation}
                />
              );
            })}
            {includesCountrySelect ? (
              <div className="hidden-large-screen margin-bottom-10">
                <Select
                  mode="multiple"
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  placeholder={"Please select"}
                  defaultValue={countrySelectDefaultValue}
                  onChange={handleCountrySelectChange}
                  filterOption={(input, option) => {
                    return (option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                >
                  {countrySelectOptions.map((o, i) => {
                    return (
                      <Option
                        key={i}
                        value={o.value}
                        disabled={
                          countrySelectDefaultValue.length > 3
                            ? countrySelectDefaultValue.includes(o.value)
                              ? false
                              : true
                            : false
                        }
                      >
                        {o.label}
                      </Option>
                    );
                  })}
                </Select>
              </div>
            ) : null}

            <CardDescription text={description} />
            <GlossaryModal>
              {includesGlossary ? (
                <div className="glossary-button-small">
                  <QuestionCircleOutlined
                    style={{
                      marginTop: "5px",
                      color: "var(--blue-primary)",
                      textAlign: "left",
                    }}
                  />{" "}
                  {MISC_TEXT["modalButtonText"][language]}
                </div>
              ) : null}
            </GlossaryModal>
          </div>

          {hideDownload ? (
            <div></div>
          ) : (
            <DownloadButton
              id={downloadableCardID}
              fileName={downloadableCardFileName}
              language={language}
              title={downloadableCardTitle}
              subtitle={downloadableCardSubtitle}
            />
          )}
        </CardMenu>
      </Col>
      <Col xs={24} lg={15}>
        {errorLoading ? (
          <ErrorLoading language={language} />
        ) : (
          <CardContent>
            {geoJSONData ? (
              <CardMap
                countryProps={countryProps}
                mapData={mapData}
                isLoading={isLoading}
                geoJSONData={geoJSONData}
                selectedDataType={selectedDataType}
                isMapJsonReady={isMapJsonReady}
                isDataAvailable={isDataAvailable}
                country={countryProps.country_text}
                language={language}
              />
            ) : (
              <>
                {includesCountrySelect ? (
                  <div className="hidden-small-screen margin-bottom-10">
                    <Select
                      mode="multiple"
                      allowClear
                      style={{
                        width: "100%",
                      }}
                      placeholder={"Please select"}
                      defaultValue={countrySelectDefaultValue}
                      onChange={handleCountrySelectChange}
                      filterOption={(input, option) => {
                        return (option?.children ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase());
                      }}
                    >
                      {countrySelectOptions.map((o, i) => {
                        return (
                          <Option
                            key={i}
                            value={o.value}
                            disabled={
                              countrySelectDefaultValue.length > 3
                                ? countrySelectDefaultValue.includes(o.value)
                                  ? false
                                  : true
                                : false
                            }
                          >
                            {o.label}
                          </Option>
                        );
                      })}
                    </Select>
                  </div>
                ) : null}

                <CardGraph
                  options={
                    language === "en" ? translateMonths(options) : options
                  }
                  isLoading={isLoading}
                  isDataAvailable={isDataAvailable}
                  country={countryProps ? countryProps.country_text : "--"}
                  borderRadius={borderRadius}
                />
              </>
            )}
          </CardContent>
        )}
      </Col>
      {hideDownload ? <></> : <div id={downloadableCardID + "-dc"}></div>}
    </Row>
  );
}
